/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds === 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

/**
 * Get badge color class based on call type
 */
export function getCallTypeBadge(type?: string): string {
  if (!type) return 'bg-gray-500';

  switch (type.toLowerCase()) {
    case 'inbound':
      return 'bg-green-500';
    case 'outbound':
      return 'bg-blue-500';
    case 'internal':
      return 'bg-yellow-500';
    default:
      return 'bg-gray-500';
  }
}

/**
 * Debounce function to limit API calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Format date for API requests
 */
export function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/**
 * Format date/time for API requests in PBX date format.
 */
export function formatDateTimeForApi(date: Date): string {
  const dateStr = formatDateForApi(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${dateStr} ${hours}:${minutes}:${seconds}`;
}

/**
 * Get today's date range for API queries
 */
export function getTodayRange(): { startTime: string; endTime: string } {
  return getDayRange(new Date());
}

/** Local calendar day 00:00:00–23:59:59 in PBX API format. */
export function getDayRange(date: Date): { startTime: string; endTime: string } {
  const dateStr = formatDateForApi(date);
  return {
    startTime: `${dateStr} 00:00:00`,
    endTime: `${dateStr} 23:59:59`,
  };
}

export function getYesterdayRange(): { startTime: string; endTime: string } {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return getDayRange(yesterday);
}

/** Monday 00:00 through end of today (local week). */
export function getThisWeekRange(): { startTime: string; endTime: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // JS: 0=Sun … 6=Sat → Monday-based week
  const day = start.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  start.setDate(start.getDate() - daysFromMonday);
  return {
    startTime: `${formatDateForApi(start)} 00:00:00`,
    endTime: `${formatDateForApi(now)} 23:59:59`,
  };
}

/** First day of this month through end of today. */
export function getThisMonthRange(): { startTime: string; endTime: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startTime: `${formatDateForApi(start)} 00:00:00`,
    endTime: `${formatDateForApi(now)} 23:59:59`,
  };
}

/** Convert PBX API datetime `YYYY/MM/DD HH:mm:ss` → HTML date input `YYYY-MM-DD`. */
export function apiDateTimeToHtmlDate(apiDateTime: string): string {
  return apiDateTime.slice(0, 10).replace(/\//g, '-');
}

/** HTML date pair for Today / This week / This month presets. */
export function getPresetHtmlDateRange(
  preset: 'today' | 'week' | 'month'
): { startDate: string; endDate: string } {
  const range =
    preset === 'today'
      ? getTodayRange()
      : preset === 'week'
        ? getThisWeekRange()
        : getThisMonthRange();
  return {
    startDate: apiDateTimeToHtmlDate(range.startTime),
    endDate: apiDateTimeToHtmlDate(range.endTime),
  };
}

/**
 * Inclusive custom range from two local calendar dates (HTML date input values: YYYY-MM-DD).
 */
export function getCustomDateRange(
  startDate: string,
  endDate: string
): { startTime: string; endTime: string } {
  const start = parseHtmlDateInput(startDate);
  const end = parseHtmlDateInput(endDate);
  if (!start || !end) {
    throw new Error('Invalid custom date range');
  }
  if (start.getTime() > end.getTime()) {
    return {
      startTime: `${formatDateForApi(end)} 00:00:00`,
      endTime: `${formatDateForApi(start)} 23:59:59`,
    };
  }
  return {
    startTime: `${formatDateForApi(start)} 00:00:00`,
    endTime: `${formatDateForApi(end)} 23:59:59`,
  };
}

/** Parse YYYY-MM-DD as a local calendar date (not UTC midnight). */
export function parseHtmlDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Format Date as YYYY-MM-DD for HTML date inputs. */
export function toHtmlDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
